import { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  Stack,
  Box,
  Heading,
  Divider,
  useToast,
} from '@chakra-ui/react';
import baseService from '../../services/base';
import SealAssetInput from '../Certification/SealAssetInput';
import builtinSeal from '../../assets/images/default-seal.svg';
import { SEAL_MODELS } from '../../resources/sealModels';

// EG-27 (rework): modal ÚNICO de configuração dos selos padrão (consolida o antigo
// "Selo padrão" + "Modelos de selo"). Duas seções:
//  1) Selo/guia PADRÃO global — fallback final de qualquer selo sem upload.
//  2) Imagens POR MODELO (ESG / ODS-Processo / ODS-Projeto) — arte default do tipo.
export default function SealModelsModal({ isOpen, onClose }) {
  const toast = useToast();
  const [defaults, setDefaults] = useState(null);
  const [models, setModels] = useState([]);

  const refreshDefaults = () =>
    baseService
      .get('/certificate/assets/default')
      .then((res) => setDefaults(res.data))
      .catch(() => setDefaults(null));

  const refreshModels = () =>
    baseService
      .get('/certificate/seal-models')
      .then((res) => setModels(Array.isArray(res.data) ? res.data : []))
      .catch(() => setModels([]));

  useEffect(() => {
    if (!isOpen) return;
    refreshDefaults();
    refreshModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const byModel = (model) => models.find((m) => m.model === model);

  // ---- Padrão global (selo + guia) ----
  const saveDefault = async (payload, okMsg) => {
    try {
      await baseService.put('/certificate/assets/default', payload);
      await refreshDefaults();
      toast({ title: okMsg, status: 'success' });
    } catch (error) {
      toast({
        title: 'Erro ao salvar',
        description: error.response?.data?.message || 'Tente novamente mais tarde',
        status: 'error',
      });
      throw error;
    }
  };

  const resetDefault = async (type, okMsg) => {
    try {
      await baseService.delete(`/certificate/assets/default/${type}`);
      await refreshDefaults();
      toast({ title: okMsg, status: 'success' });
    } catch (error) {
      toast({
        title: 'Erro ao resetar',
        description: error.response?.data?.message || 'Tente novamente mais tarde',
        status: 'error',
      });
      throw error;
    }
  };

  // ---- Imagem por modelo ----
  const saveModelSeal = async (model, asset) => {
    try {
      await baseService.put(`/certificate/seal-models/${model}`, { seal: asset });
      await refreshModels();
      toast({ title: 'Selo do modelo atualizado', status: 'success' });
    } catch (error) {
      toast({
        title: 'Erro ao salvar o selo',
        description: error.response?.data?.message || 'Tente novamente mais tarde',
        status: 'error',
      });
      throw error;
    }
  };

  const resetModelSeal = async (model) => {
    try {
      await baseService.delete(`/certificate/seal-models/${model}/seal`);
      await refreshModels();
      toast({ title: 'Selo do modelo removido', status: 'success' });
    } catch (error) {
      toast({
        title: 'Erro ao remover o selo',
        description: error.response?.data?.message || 'Tente novamente mais tarde',
        status: 'error',
      });
      throw error;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered scrollBehavior='inside'>
      <ModalOverlay />
      <ModalContent maxW='620px' p='32px'>
        <Text fontSize='24px' fontWeight={700} mb='8px'>
          Selos padrão
        </Text>
        <Text fontSize='14px' color='#5C6066'>
          Configure a arte padrão de cada tipo de selo (ESG e ODS) e a guia de
          uso. Uma certificação sem selo próprio usa a arte do seu tipo; sem
          arte, cai no selo embutido.
        </Text>

        <ModalBody p='24px 0'>
          <Stack spacing='24px'>
            {SEAL_MODELS.map(({ value }) => {
              const current = byModel(value);
              return (
                <Box
                  key={value}
                  border='2px solid #EAEEF0'
                  borderRadius='12px'
                  p='20px'
                >
                  <Heading as='h4' fontSize='15px' fontWeight={600} mb='16px'>
                    Selo padrão {value}
                  </Heading>
                  <SealAssetInput
                    accept='.png,.jpg,.jpeg,.svg'
                    currentUrl={current?.url}
                    defaultUrl={builtinSeal}
                    hint='Sem upload, usa-se o selo embutido.'
                    onUpload={(asset) => saveModelSeal(value, asset)}
                    onReset={
                      current?.url ? () => resetModelSeal(value) : undefined
                    }
                  />
                </Box>
              );
            })}

            <Divider />

            <Box>
              <SealAssetInput
                label='Guia padrão (PDF)'
                noun='guia'
                kind='file'
                accept='.pdf'
                currentFilename={defaults?.guide?.filename}
                hint='PDF com instruções de uso do selo.'
                onUpload={(asset) =>
                  saveDefault({ guide: asset }, 'Guia padrão atualizada')
                }
                onReset={() => resetDefault('guide', 'Guia padrão removida')}
              />
            </Box>
          </Stack>
        </ModalBody>

        <ModalFooter p={0} mt='8px'>
          <Button
            variant='primary'
            size='large'
            w='100%'
            height='56px'
            onClick={onClose}
          >
            Concluir
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
